import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { createContext, forwardRef, useRef, useState, useContext, cloneElement } from 'react';
import { mergeProps, useButton } from 'react-aria';

import { mergeRefs } from '../../lib/mergeRefs';
import { Glyph } from '../common/Glyph';

import { useHover } from '../../hooks/useHover';

import { su } from '../../theme/functions/su';
import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';
import glyphStyles from '../common/Glyph.module.css';

import { Button } from './Button';

const DualButtonRoundContext = createContext(null);

export function DualButtonRound(props) {
  const [partHoveredState, setPartHoveredState] = useState();
  const [partPressedState, setPartPressedState] = useState();

  const [firstChild, secondChild] = props.children;

  // Provide context to DualButtonRound.Parts so they can act as a pair.
  return (
    <DualButtonRoundContext.Provider value={{ setPartPressedState, setPartHoveredState }}>
      <DualButtonRound.Root
        data-part-hovered={partHoveredState}
        data-part-pressed={partPressedState}
      >
        {cloneElement(firstChild, { partPosition: 'left' })}
        <DualButtonRound.Divider />
        {cloneElement(secondChild, { partPosition: 'right' })}
      </DualButtonRound.Root>
    </DualButtonRoundContext.Provider>
  );
}

DualButtonRound.Part = forwardRef((props, forwardedRef) => {
  const ref = useRef();
  const state = useContext(DualButtonRoundContext);

  // Props always prefers useButton.
  const button = useButton(
    {
      ...props,
      // Explicit set. Note useHover.
      isDisabled: props.isDisabled,
      onPressStart(event) {
        props.onPressStart?.(event);
        state?.setPartPressedState(props.partPosition);
      },
      onPressEnd(event) {
        props.onPressEnd?.(event);
        state?.setPartPressedState(null);
      },
    },
    ref,
  );

  const hover = useHover({
    // Explicit set. Note useButton.
    isDisabled: props.isDisabled,
    onHoverStart() {
      state?.setPartHoveredState(props.partPosition);
    },
    onHoverEnd() {
      state?.setPartHoveredState(null);
    },
  });

  return (
    <DualButtonRound.Button
      {...mergeProps(button.buttonProps, hover.hoverProps)}
      ref={mergeRefs([ref, forwardedRef])}
      title={props.title}
      className={props.className}
      style={props.style}
      data-is-disabled={props.isDisabled}
      data-is-pressed={button.isPressed}
    >
      {props.children}
    </DualButtonRound.Button>
  );
});

DualButtonRound.propTypes = {
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
};

DualButtonRound.Part.propTypes = {
  ...Button.propTypes,
  // override: Mark title as required since this button only has icons.
  title: PropTypes.string.isRequired,
};

DualButtonRound.Text = styled.span`
  white-space: nowrap;
`;

DualButtonRound.Icon = Glyph;

DualButtonRound.Divider = styled.div`
  position: relative;
  width: 1px;
  height: ${su(5)};
  border-top: ${su(1)} solid ${theme.color.component};
  border-bottom: ${su(1)} solid ${theme.color.component};
  background: ${theme.color.line};
  z-index: 15;
`;

DualButtonRound.Button = styled.button`
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 8px;
  min-width: ${su(5)};
  padding: ${su(1)};
  line-height: ${su(2)};
  background: ${theme.color.component};
  transition: ${motion.duration.fast} ${motion.curve.fast_in};
  transition-property: background-color;
  z-index: 10;

  &:first-of-type {
    border-radius: ${su(2.5)} 0 0 ${su(2.5)};
  }

  &:last-of-type {
    border-radius: 0 ${su(2.5)} ${su(2.5)} 0;
  }

  .${glyphStyles.root} {
    transition: ${motion.duration.fast} ${motion.curve.fast_in};
    transition-property: transform;
  }

  // States
  &:not([data-is-disabled='true']) {
    &:hover {
      background-color: ${theme.monoButton.background_hover};
    }

    &[data-is-pressed='true'] {
      .${glyphStyles.root} {
        transform: scale(0.875);
      }
    }
  }

  &[data-is-disabled='true'] {
    cursor: not-allowed;

    .${glyphStyles.root} {
      color: ${theme.icon.color_disabled};
    }
  }
`;

DualButtonRound.Root = styled.div`
  position: relative;
  z-index: 5;
  display: flex;
  background: ${theme.color.component};
  box-shadow: ${theme.boxShadow.default};
  border-radius: ${su(2.5)};
  transform: perspective(300px) rotateY(0deg);
  transform-origin: center center;
  transition: ${motion.duration.fast} ${motion.curve.fast_in};
  transition-property: transform;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: ${su(2.5)};
    box-shadow: ${theme.boxShadow.default_hover_combo};
    opacity: 0;
    transition: ${motion.duration.fast} ${motion.curve.fast_in};
    transition-property: opacity;
    z-index: -1;
  }

  &::before {
    transform: perspective(300px) rotateY(45deg);
    transform-origin: left center;
  }

  &::after {
    transform: perspective(300px) rotateY(-45deg);
    transform-origin: right center;
  }

  &[data-part-hovered='left']:not([data-part-pressed='left']) {
    &::before {
      opacity: 1;
    }
  }

  &[data-part-hovered='right']:not([data-part-pressed='right']) {
    &::after {
      opacity: 1;
    }
  }

  &[data-part-pressed='left'] {
    //transform: perspective(300px) rotateY(-3deg);
    //transform-origin: right center;

    &::before {
      box-shadow: ${theme.boxShadow.default_active_combo};
      opacity: 1;
    }
  }

  &[data-part-pressed='right'] {
    //transform: perspective(300px) rotateY(3deg);
    //transform-origin: left center;

    &::after {
      box-shadow: ${theme.boxShadow.default_active_combo};
      opacity: 1;
    }
  }
`;
